import Cookies from 'js-cookie';
import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import type { RouteLocationNamedRaw } from 'vue-router';
import { useRouter } from 'vue-router';
import type { IEventLike } from '@/interface/IEventLike';
import type { MarketTab, MatchFunction } from '@/interface/enum';
import { ProductName, RoutePage, SportType } from '@/interface/enum';
import { useApp } from '@/libraries/composables/useApp';
import { usePlatformActions } from '@/libraries/composables/usePlatformActions';
import { platform } from '@/libraries/helpers/platformHelper';
import { getTranslated } from '@/libraries/helpers/translationHelper';
import { i18n } from '@/libraries/i18n';
import { buildStatisticUrl, getHost, getSportsBsiUrl } from '@/libraries/url';
import { useProductPlatformStore } from '@/stores/productPlatformStore';
import { useRootStore } from '@/stores/rootStore';

export function useMatchFunction(matchInfo: Ref<IEventLike | null>) {
    const { isAsi } = storeToRefs(useRootStore());
    const { isComponentInPp } = storeToRefs(useProductPlatformStore());

    const getMatchDetailRoute = (event: IEventLike, options?: IGoToMatchDetailOptions) => {
        const leagueName = getTranslated(event.leagueName);
        const homeTeam = getTranslated(event.homeTeam?.teamName);
        const awayTeam = getTranslated(event.awayTeam?.teamName);
        const route = {
            name: RoutePage.MatchDetail,
            params: {
                sportType: SportType.toRouteName(event.sportType),
                leagueName: leagueName.replace(/ /g, '-'),
                matchName: `${homeTeam.replace(/ /g, '-')}-vs-${awayTeam.replace(/ /g, '-')}`,
                matchId: event.eventId,
            },
            query: {},
        };

        if (options?.marketTab) {
            route.query = { tab: options.marketTab };
        }

        if (options?.matchFunctionTab) {
            route.query = { mf: options.matchFunctionTab };
        }

        if (!isAsi.value) {
            route.query = { ...route.query, isDirectIn: 'true' };
        }

        return route;
    };

    const setCookie = (matchDetailRoute: RouteLocationNamedRaw) => {
        const oneMinute = new Date(new Date().getTime() + 1 * 60 * 1000);
        Cookies.set('toSportsRoute', JSON.stringify(matchDetailRoute), {
            domain: getHost(),
            expires: oneMinute,
        });
    };

    const router = useRouter();
    const shouldShowLogin = (hasMoreMarketAccess: boolean) => !isAsi.value && !hasMoreMarketAccess;

    const navigateFromPp = () => {
        if (isAsi.value) {
            platform.switchProduct({ productName: ProductName.Sports });
        } else {
            window.location.href = getSportsBsiUrl();
        }
    };

    interface IGoToMatchDetailOptions {
        marketTab?: MarketTab;
        matchFunctionTab?: MatchFunction;
    }
    const goToMatchDetail = (options?: IGoToMatchDetailOptions) => {
        if (!matchInfo.value) {
            return;
        }

        const route = getMatchDetailRoute(matchInfo.value, options);
        const hasMoreMarketAccess = !options?.matchFunctionTab;

        if (!isAsi.value) {
            setCookie(route);
        }

        if (shouldShowLogin(hasMoreMarketAccess)) {
            platform.showLogin('Sports');
            return;
        }

        if (isComponentInPp.value) {
            navigateFromPp();
            return;
        }

        router.push(route);
    };

    return {
        goToMatchDetail,
    };
}

const { openStatisticsInApp } = useApp();
const { handleActionByPlatform } = usePlatformActions();

export function openStatistics(matchId: number) {
    const { isAsi, brandName } = storeToRefs(useRootStore());

    if (isAsi.value) {
        const url = buildStatisticUrl(matchId, i18n.locale.value, brandName.value.toLowerCase());

        handleActionByPlatform({
            appAction: () => openStatisticsInApp(url),
            webAction: () => {
                window.open(
                    url,
                    '_blank',
                    'toolbar=no,scrollbars=no,resizable=no,location=no,width=1000,height=600',
                );
            },
        });
    } else {
        platform.showLogin('Sports');
    }
}
